import {
  Instant,
  LocalDate,
  LocalTime,
  ZoneId,
  ZonedDateTime,
} from "@js-joda/core";
import "@js-joda/timezone";

const STOCKHOLM_TIME_ZONE = ZoneId.of("Europe/Stockholm");

/** Returns the current date in Stockholm local time. */
export const getStockholmLocalDateNow = (): LocalDate =>
  LocalDate.now(STOCKHOLM_TIME_ZONE);

type TimeParts = {
  hours: number;
  minutes: number;
};

interface TimeReportTimes {
  startTime: string;
  endTime: string;
  breakStartTime: string;
  breakEndTime: string;
}

/** Format a number as a two-digit string. */
const toTwoDigits = (value: number): string =>
  value.toString().padStart(2, "0");

/** Parse an ISO date/time string into a Stockholm-zoned date-time. */
const toStockholmZonedDateTime = (
  value: string | null | undefined,
): ZonedDateTime | null => {
  if (!value) return null;
  try {
    const instant = Instant.parse(value);
    return ZonedDateTime.ofInstant(instant, STOCKHOLM_TIME_ZONE);
  } catch {
    return null;
  }
};

/** Resolve if current Stockholm local day-of-month is before a given day number. */
export const isBeforeDayOfMonthInStockholm = (
  dayOfMonth: number,
  now: Date = new Date(),
): boolean => {
  const normalizedDay = Math.max(1, Math.min(31, Math.floor(dayOfMonth)));
  const zonedNow = ZonedDateTime.ofInstant(
    Instant.ofEpochMilli(now.getTime()),
    STOCKHOLM_TIME_ZONE,
  );
  return zonedNow.dayOfMonth() < normalizedDay;
};

export const isAdjustableMonth = (
  displayedMonth: LocalDate,
  now: Date = new Date(),
): boolean => {
  const stockholmToday = ZonedDateTime.ofInstant(
    Instant.ofEpochMilli(now.getTime()),
    STOCKHOLM_TIME_ZONE,
  ).toLocalDate();

  const currentMonthStart = stockholmToday.withDayOfMonth(1);
  const displayedMonthStart = displayedMonth.withDayOfMonth(1);
  const previousMonthStart = currentMonthStart.minusMonths(1);

  if (displayedMonthStart.equals(currentMonthStart)) return true;
  if (displayedMonthStart.equals(previousMonthStart)) {
    return isBeforeDayOfMonthInStockholm(11, now);
  }
  return false;
};

/** Convert an ISO date/time string to a JavaScript Date in Stockholm time. */
const toStockholmDate = (value: string | null | undefined): Date | null => {
  const zonedDateTime = toStockholmZonedDateTime(value);
  if (!zonedDateTime) return null;
  return new Date(zonedDateTime.toInstant().toEpochMilli());
};

/** Convert a LocalDate to a JavaScript Date at Stockholm midnight. */
const toStockholmDateFromLocalDate = (value: LocalDate): Date => {
  const zonedDateTime = ZonedDateTime.of(
    value,
    LocalTime.MIDNIGHT,
    STOCKHOLM_TIME_ZONE,
  );
  return new Date(zonedDateTime.toInstant().toEpochMilli());
};

const addStockholmDaysToIso = (
  value: string,
  dayCount: number,
): string | null => {
  const zonedDateTime = toStockholmZonedDateTime(value);
  if (!zonedDateTime) return null;
  return zonedDateTime.plusDays(dayCount).toInstant().toString();
};

const isIsoEarlierThan = (
  leftValue: string,
  rightValue: string,
): boolean | null => {
  const leftMillis = parseIsoMillis(leftValue);
  const rightMillis = parseIsoMillis(rightValue);
  if (leftMillis == null || rightMillis == null) return null;
  return leftMillis < rightMillis;
};

const isIsoEarlierThanOrEqual = (
  leftValue: string,
  rightValue: string,
): boolean | null => {
  const leftMillis = parseIsoMillis(leftValue);
  const rightMillis = parseIsoMillis(rightValue);
  if (leftMillis == null || rightMillis == null) return null;
  return leftMillis <= rightMillis;
};

/** Parse a strict HH:mm time input value into numeric parts. */
const parseTimeInputValue = (value: string): TimeParts | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }
  return { hours, minutes };
};

/** Format an ISO date/time string as a localized time label (HH:mm). */
export const formatTimeLabel = (
  value: string | null | undefined,
  locale: string,
): string => {
  const parsed = toStockholmDate(value);
  if (!parsed) return "--";
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(parsed);
};

/**
 * Format a date-like string as a localized Stockholm calendar date.
 * Returns "-" for empty input and returns original value if parsing fails.
 */
export const formatDateLabelInStockholm = (
  value: string | null | undefined,
  locale: string,
): string => {
  if (!value) return "-";

  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateMatch) {
    const year = Number(isoDateMatch[1]);
    const month = Number(isoDateMatch[2]);
    const day = Number(isoDateMatch[3]);
    const candidateDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const isValidIsoDate =
      candidateDate.getUTCFullYear() === year &&
      candidateDate.getUTCMonth() === month - 1 &&
      candidateDate.getUTCDate() === day;
    if (!isValidIsoDate) return value;

    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: STOCKHOLM_TIME_ZONE.id(),
    }).format(candidateDate);
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(parsedDate);
};

/** Format start and end ISO date/time values as a localized time range. */
export const formatTimeRangeLabel = (
  start: string | null | undefined,
  end: string | null | undefined,
  locale: string,
): string => {
  if (!start || !end) return "--";
  return `${formatTimeLabel(start, locale)} - ${formatTimeLabel(end, locale)}`;
};

/** Format an ISO date/time string as a localized weekday/date label. */
export const formatDateWithWeekdayLabel = (
  value: string | null | undefined,
  locale: string,
): string => {
  const parsed = toStockholmDate(value);
  if (!parsed) return "--";
  const formatted = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(parsed);
  return formatted.length > 0
    ? formatted.charAt(0).toUpperCase() + formatted.slice(1)
    : formatted;
};

/** Format an ISO date/time string as a localized short date-time label. */
export const formatDateTimeShortLabel = (
  value: string | null | undefined,
  locale: string,
): string => {
  const parsed = toStockholmDate(value);
  if (!parsed) return "--";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(parsed);
};

/** Format an ISO date/time string as localized day + month text. */
export const formatDayMonthLabel = (
  value: string | null | undefined,
  locale: string,
): string => {
  const parsed = toStockholmDate(value);
  if (!parsed) return "--";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(parsed);
};

/** Format an ISO date/time string as a localized weekday with capitalized first letter. */
export const formatWeekdayLabel = (
  value: string | null | undefined,
  locale: string,
): string => {
  const parsed = toStockholmDate(value);
  if (!parsed) return "";
  const raw = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(parsed);
  return raw.length > 0 ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
};

/** Format a LocalDate as YYYYMMDD for API requests. */
export const formatLocalDateAsApiDate = (value: LocalDate): string => {
  const month = String(value.monthValue()).padStart(2, "0");
  const day = String(value.dayOfMonth()).padStart(2, "0");
  return `${value.year()}${month}${day}`;
};

/** Parse an ISO date or ISO date-time string into LocalDate (date part only). */
export const parseIsoDateToLocalDateOrNull = (
  value: string | null | undefined,
): LocalDate | null => {
  if (!value) return null;

  try {
    const instant = Instant.parse(value);
    return ZonedDateTime.ofInstant(instant, STOCKHOLM_TIME_ZONE).toLocalDate();
  } catch {}

  const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!dateMatch) return null;

  try {
    return LocalDate.parse(dateMatch[1]);
  } catch {
    return null;
  }
};

/**
 * Convert Stockholm local midnight for a LocalDate to UTC ISO instant.
 * Example: 2026-06-30 -> 2026-06-29T22:00:00Z (summer time, UTC+2)
 */
export const toIsoStartOfDayUtcFromStockholmDate = (
  value: LocalDate,
): string => {
  return ZonedDateTime.of(
    value,
    LocalTime.MIDNIGHT,
    STOCKHOLM_TIME_ZONE,
  ).toInstant().toString();
};

/** Format a LocalDate as localized month label with capitalized first letter. */
export const formatLocalDateMonthLabel = (
  value: LocalDate,
  locale: string,
): string => {
  const jsDate = toStockholmDateFromLocalDate(value);
  const raw = new Intl.DateTimeFormat(locale, {
    month: "long",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(jsDate);
  return raw.length > 0 ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
};

/** Format a LocalDate as localized weekday label with capitalized first letter. */
export const formatLocalDateWeekdayLabel = (
  value: LocalDate,
  locale: string,
  weekday: "long" | "short" = "long",
): string => {
  const jsDate = toStockholmDateFromLocalDate(value);
  const raw = new Intl.DateTimeFormat(locale, {
    weekday,
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(jsDate);
  return raw.length > 0 ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
};

/** Format a LocalDate as localized weekday/day/month/year with capitalized first letter. */
export const formatLocalDateWithWeekdayLabel = (
  value: LocalDate,
  locale: string,
): string => {
  const jsDate = toStockholmDateFromLocalDate(value);
  const raw = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(jsDate);
  return raw.length > 0 ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
};

/** Format a LocalDate as localized month/year with capitalized first letter. */
export const formatLocalDateMonthYearLabel = (
  value: LocalDate,
  locale: string,
): string => {
  const jsDate = toStockholmDateFromLocalDate(value);
  const raw = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(jsDate);
  return raw.length > 0 ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
};

/** Format a LocalDate range as localized day/month/year range text. */
export const formatLocalDateRangeLabel = (
  fromValue: LocalDate,
  toValue: LocalDate,
  locale: string,
): string => {
  const fromDate = toStockholmDateFromLocalDate(fromValue);
  const toDate = toStockholmDateFromLocalDate(toValue);
  const formatter = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  });
  const rangeFormat = formatter.formatRange;
  if (typeof rangeFormat === "function") {
    return rangeFormat.call(formatter, fromDate, toDate);
  }
  return `${formatter.format(fromDate)} - ${formatter.format(toDate)}`;
};

/** Calculate whole minutes between two ISO date/time strings. */
export const minutesBetweenIso = (
  startValue?: string | null,
  endValue?: string | null,
): number => {
  const startMillis = parseIsoMillis(startValue);
  const endMillis = parseIsoMillis(endValue);
  if (startMillis == null || endMillis == null || endMillis <= startMillis) {
    return 0;
  }
  return Math.floor((endMillis - startMillis) / 60000);
};

/** Parse an ISO date/time string into epoch milliseconds, or null if invalid. */
export const parseIsoMillis = (value?: string | null): number | null => {
  if (!value) return null;
  try {
    return Instant.parse(value).toEpochMilli();
  } catch {
    return null;
  }
};

/**
 * Calculate whole minutes between two ISO date/time strings.
 * Returns null when any value is invalid or end is not after start.
 */
export const minutesBetweenIsoStrict = (
  startValue?: string | null,
  endValue?: string | null,
): number | null => {
  const startMillis = parseIsoMillis(startValue);
  const endMillis = parseIsoMillis(endValue);
  if (startMillis == null || endMillis == null || endMillis <= startMillis) {
    return null;
  }
  return Math.floor((endMillis - startMillis) / 60000);
};

/** Calculate signed whole-minute difference (left - right) between two ISO date/time strings. */
export const signedMinutesBetweenIso = (
  leftValue?: string | null,
  rightValue?: string | null,
): number | null => {
  const leftMillis = parseIsoMillis(leftValue);
  const rightMillis = parseIsoMillis(rightValue);
  if (leftMillis == null || rightMillis == null) return null;
  return Math.trunc((leftMillis - rightMillis) / 60000);
};

/**
 * Parse a signed HH:mm or HH:mm:ss duration string into minutes.
 * Examples: "01:30" => 90, "-01:30" => -90.
 */
export const parseSignedDurationToMinutes = (
  value?: string | null,
): number | null => {
  if (!value) return null;
  const normalized = value.trim().replace("−", "-");
  const match = normalized.match(/^([+-])?(\d+):(\d{2})(?::\d{2})?$/);
  if (!match) return null;
  const signToken = match[1];
  const hours = Number(match[2]);
  const minutes = Number(match[3]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (minutes < 0 || minutes > 59) return null;
  const sign = signToken === "-" ? -1 : 1;
  return sign * (hours * 60 + minutes);
};

/** Format minute duration as h/m text (e.g. 1h 30m, 45m). */
export const formatMinutesDuration = (minutes: number): string => {
  const safeMinutes = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safeMinutes / 60);
  const rest = safeMinutes % 60;
  if (hours === 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
};

/** Format minute duration with sign prefix. */
export const formatSignedMinutesDuration = (minutes: number): string => {
  if (minutes === 0) return "0h";
  const prefix = minutes > 0 ? "+" : "-";
  return `${prefix}${formatMinutesDuration(Math.abs(minutes))}`;
};

/**
 * Format a signed duration string (HH:mm or HH:mm:ss) for summary cards.
 * Returns "0h" for empty/invalid/zero values, omits plus prefix for positive values,
 * and keeps minus prefix for negative values.
 */
export const formatSummaryDuration = (value?: string | null): string => {
  const totalMinutes = parseSignedDurationToMinutes(value);
  if (totalMinutes == null || totalMinutes === 0) return "0h";
  const prefix = totalMinutes < 0 ? "-" : "";
  const normalized = formatMinutesDuration(Math.abs(totalMinutes));
  if (normalized === "0m") return "0h";
  return `${prefix}${normalized}`;
};

/** Convert an ISO date/time string to a HH:mm input value in Stockholm time. */
export const toTimeInputValue = (value: string | null | undefined): string => {
  const zonedDateTime = toStockholmZonedDateTime(value);
  if (!zonedDateTime) return "";
  return `${toTwoDigits(zonedDateTime.hour())}:${toTwoDigits(
    zonedDateTime.minute(),
  )}`;
};

/** Resolve initial HH:mm input from primary value with fallback value. */
export const resolveInitialTimeInputValue = (
  primary: string | null | undefined,
  fallback: string | null | undefined,
): string => {
  const primaryValue = toTimeInputValue(primary);
  if (primaryValue.length > 0) return primaryValue;
  return toTimeInputValue(fallback);
};

/** Merge date from an ISO value with a HH:mm input and return normalized ISO. */
export const mergeIsoDateWithTimeInput = (
  sourceIso: string | null | undefined,
  timeValue: string,
): string | null => {
  const baseDateTime = toStockholmZonedDateTime(sourceIso);
  const parsedTime = parseTimeInputValue(timeValue);
  if (!baseDateTime || !parsedTime) return null;

  const nextDateTime = baseDateTime.with(
    LocalTime.of(parsedTime.hours, parsedTime.minutes),
  );
  return nextDateTime.toInstant().toString();
};

/**
 * Normalizes report/break times for overnight shifts.
 * Any time earlier than reported start is interpreted as next day.
 */
export const normalizeTimeReportTimesOvernight = (
  value: TimeReportTimes,
): TimeReportTimes | null => {
  let normalizedEnd = value.endTime;
  let normalizedBreakStart = value.breakStartTime;
  let normalizedBreakEnd = value.breakEndTime;

  const isEndEarlierOrEqual = isIsoEarlierThanOrEqual(
    normalizedEnd,
    value.startTime,
  );
  if (isEndEarlierOrEqual == null) return null;
  if (isEndEarlierOrEqual) {
    const nextEnd = addStockholmDaysToIso(normalizedEnd, 1);
    if (!nextEnd) return null;
    normalizedEnd = nextEnd;
  }

  const isBreakStartEarlierThanStart = isIsoEarlierThan(
    normalizedBreakStart,
    value.startTime,
  );
  if (isBreakStartEarlierThanStart == null) return null;
  if (isBreakStartEarlierThanStart) {
    const nextBreakStart = addStockholmDaysToIso(normalizedBreakStart, 1);
    if (!nextBreakStart) return null;
    normalizedBreakStart = nextBreakStart;
  }

  const isBreakEndEarlierThanStart = isIsoEarlierThan(
    normalizedBreakEnd,
    value.startTime,
  );
  if (isBreakEndEarlierThanStart == null) return null;
  if (isBreakEndEarlierThanStart) {
    const nextBreakEnd = addStockholmDaysToIso(normalizedBreakEnd, 1);
    if (!nextBreakEnd) return null;
    normalizedBreakEnd = nextBreakEnd;
  }

  const isBreakEndEarlierThanBreakStart = isIsoEarlierThan(
    normalizedBreakEnd,
    normalizedBreakStart,
  );
  if (isBreakEndEarlierThanBreakStart == null) return null;
  if (isBreakEndEarlierThanBreakStart) {
    const nextBreakEnd = addStockholmDaysToIso(normalizedBreakEnd, 1);
    if (!nextBreakEnd) return null;
    normalizedBreakEnd = nextBreakEnd;
  }

  return {
    startTime: value.startTime,
    endTime: normalizedEnd,
    breakStartTime: normalizedBreakStart,
    breakEndTime: normalizedBreakEnd,
  };
};
